import { useTenantFeatures } from '../../../shared/use-tenant-features';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight, PenLine } from 'lucide-react';
import { Button } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { HandoverCard, HandoverIcon } from './handover-ui';
import { clock, useHandoverLines } from './use-handover';

/**
 * De strook bovenaan Home (Stap 39, OVD-2, spec §3 frame 1).
 *
 * Eerste element op Home, boven de mededelingen, en alleen voor wie nu in de ploeg zit die de
 * overdracht moet lezen. Ze blijft staan tot hij "Gelezen" tikt - de server beslist dat, en de
 * zin is dezelfde als in de pushmelding en de meldingenlijst.
 */
export function AppHandoverBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lines = useHandoverLines();
  const { isOn } = useTenantFeatures();

  const stroken = (lines.data ?? []).filter((line) => line.banner !== null);
  // Een bedrijf zonder ploegoverdracht (ORG-1) ziet de strook niet, ook als er oude lijnen bestaan.
  if (stroken.length === 0 || !isOn('shift_handover.handovers')) return null;

  return (
    <>
      {stroken.map((line) => {
        const banner = line.banner!;
        return (
          <HandoverCard
            key={line.lineId}
            className="border-2 border-[var(--brand-orange-aa)] bg-brand-orange-tint"
            data-testid="handover-banner"
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[var(--brand-orange-aa)] text-text-on-dark"
              >
                <HandoverIcon size={24} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-title-sm font-semibold text-text-primary">
                  {banner.title}
                </p>
                {banner.submittedAt && banner.authorName ? (
                  <p className="text-small text-text-secondary">
                    {t('handover.bannerMeta', {
                      time: clock(banner.submittedAt, i18n.language),
                      name: banner.authorName,
                    })}
                  </p>
                ) : null}
              </div>
            </div>
            <p className="flex flex-wrap gap-x-3 text-small font-semibold text-text-primary">
              {banner.facts.join(' · ')}
            </p>
            <Button
              variant="urgent"
              size="lg"
              className="w-full"
              data-testid="handover-banner-open"
              onClick={() =>
                void (banner.handoverId
                  ? navigate({
                      to: '/app/overdracht/$handoverId',
                      params: { handoverId: banner.handoverId },
                    })
                  : navigate({
                      to: '/app/overdracht/lijn/$lineId',
                      params: { lineId: line.lineId },
                    }))
              }
            >
              {banner.handoverId ? t('handover.bannerRead') : t('handover.bannerOpenItems')}
            </Button>
          </HandoverCard>
        );
      })}
    </>
  );
}

/**
 * De ingang om te schrijven, onder "Acties" op Home (niet getekend; `gaps.md`).
 *
 * De spec tekent hoe je de overdracht leest, niet hoe je aan het einde van je dienst bij het
 * schrijfscherm komt. Deze kaart verschijnt alleen voor wie bij een lijn hoort en nu een dienst
 * heeft - de vorm is die van de andere actiekaarten.
 */
export function AppHandoverWriteCard() {
  const { t } = useTranslation();
  const { has } = useSession();
  const lines = useHandoverLines();
  if (!has('shift_handover.handover.write')) return null;

  const metDienst = (lines.data ?? []).filter((line) => line.currentSlotId !== null);
  if (metDienst.length === 0) return null;

  return (
    <>
      {metDienst.map((line) => {
        const slot = line.slots.find((candidate) => candidate.id === line.currentSlotId);
        return (
          <HandoverCard key={line.lineId} data-app-card data-testid="handover-write-card">
            <Link
              to="/app/overdracht/schrijven/$lineId/$slotId"
              params={{ lineId: line.lineId, slotId: line.currentSlotId as string }}
              className="flex items-center gap-element"
            >
              <span
                aria-hidden
                className="rounded-pill bg-brand-orange-tint p-2 text-[var(--orange-tint-text-aa)]"
              >
                <PenLine className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-heading text-content font-semibold text-text-primary">
                  {t('handover.writeCard')}
                </span>
                <span className="block text-small text-text-muted">
                  {line.lineName} · {slot?.name ?? ''}
                </span>
              </span>
              <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-text-muted" />
            </Link>
          </HandoverCard>
        );
      })}
    </>
  );
}
